import React, { useState, useEffect, FormEvent } from "react";
import moment from "moment";
import {
  Panel,
  DataTable,
  DataTableColumn,
  NewTabLink,
  Button,
  HelpIconMode,
  DataTableState,
  Checkbox,
  Tooltip,
  DataTableSorting,
} from "@countysuite/ui";
import { LackOfProsecutionCaseSearchResult } from "../LackOfProsecutionTypes";
import { LackOfProsecutionLettersModal } from "../LettersModal/LackOfProsecutionLettersModal";
import { DropdownCategory, Official } from "types/CivilTypes";
import { OkSign, QuestionSign, CancelSign } from "components/icons";
import { HelpIconButton } from "components/HelpIconButton/HelpIconButton";
import { boolean, string } from "yup";
import { Row, Col } from "react-bootstrap";
import { SSL_OP_SSLEAY_080_CLIENT_DH_BUG } from "constants";
import { Http } from "utils/Http";
import { ToggleButton } from "primereact/togglebutton";
import { stat } from "fs";

export interface Props {
  casesInTable: LackOfProsecutionCaseSearchResult[];
  casePageUrl: string;
  letterCategories: DropdownCategory[];
  signatures: DropdownCategory[];
  defaultSignatureId: number;
  generateLetters: (
    selectedCaseID: number | undefined,
    letterId: number,
    deadlineDate: string,
    notificationDate: string,
    signatureId: number,
    letterIds: number[],
    pretrialDate: string
  ) => void;
  undoLetters: (
    selectedCaseID: number | undefined,
    letterId: number,
    letterIds: number[]
  ) => void;
  combineLetters: (
    letterId: number,
    deadlineDate: string,
    notificationDate: string,
    signatureId: number,
    letterIds: number[],
    pretrialDate: string
  ) => void;
  reloadExistingCaseResults: (
    page: number,
    pageSize: number,
    sorted: any[],
    filtered: any[],
    showOnlyDocketedCases: boolean
  ) => void;
  addLetterID: (caseId: number) => void;
  removeLetterID: (caseId: number) => void;
  totalPages?: number;
  totalRows?: number;
  loading?: boolean;
  hideDeadlineDate: boolean;
  showLetterSelect: boolean;
  caseIds: number[];
  showPretrialDatePicker: boolean;
}

export const LackOfProsecutionGenerateLettersPanel: React.FC<Props> = (
  props
) => {
  const { casesInTable } = props;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorted, setSorted] = useState<DataTableSorting[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [showOnlyEventsToggle, setShowOnlyEventsToggle] = useState<boolean>(
    false
  );
  const [chosenCase, setChosenCase] = useState<
    LackOfProsecutionCaseSearchResult | undefined
  >();

  useEffect(
    () =>
      props.reloadExistingCaseResults(
        page,
        pageSize,
        sorted,
        [{ id: "global", value: filter }],
        showOnlyEventsToggle
      ),
    [page, pageSize, sorted, filter, showOnlyEventsToggle]
  );

  const getEventDate = (isoDateString: string) => {
    return moment(isoDateString, "YYYY-MM-DD HH:mm:ss").format("MM/DD/YYYY");
  };

  const showLetterModal = (row?: LackOfProsecutionCaseSearchResult) => {
    setChosenCase(row);
    setShowModal(true);
  };

  const hideLetterModal = () => {
    setChosenCase(undefined);
    setShowModal(false);
  };

  const onFetchData = async (state: DataTableState, globalFilter: string) => {
    setPage(state.page);
    setPageSize(state.pageSize);
    setSorted(state.sorted);
    setFilter(globalFilter);
  };

  const columns: DataTableColumn<LackOfProsecutionCaseSearchResult>[] = [
    {
      Header: "Case Number",
      id: "casenumber_col",
      style: { flexBasis: 2 },
      width: 140,
      Cell: (cell) => {
        const row: LackOfProsecutionCaseSearchResult = cell.original;
        const link = `${props.casePageUrl}?caseId=${row.caseID}`;
        return <NewTabLink href={link}>{row.caseNumber}</NewTabLink>;
      },
    },
    {
      Header: "Case Caption",
      id: "casecaption_col",
      style: { whiteSpace: "normal", flexBasis: 4 },
      Cell: (cell) => {
        const row: LackOfProsecutionCaseSearchResult = cell.original;
        const link = `${props.casePageUrl}?caseId=${row.caseID}`;
        return <NewTabLink href={link}>{row.caseCaption}</NewTabLink>;
      },
    },
    {
      Header: "Case Category",
      id: "casecategory_col",
      style: { whiteSpace: "normal" },
      width: 160,
      key: "caseCategoryName",
      accessor: (row) => row.caseCategoryName || "N/A",
    },
    {
      Header: "Dockets",
      id: "docketsCol",
      style: {
        textAlign: "center",
        whiteSpace: "normal",
        flexBasis: 8,
      },
      Cell: (cell) => {
        if (!cell || !cell.original) return null;
        let row: LackOfProsecutionCaseSearchResult = cell.original;
        if (!row.caseEvents || !row.caseEvents.length)
          return <span>No dockets generated.</span>;

        return (
          <ol className="lop-table-list">
            {row.caseEvents.map((ce) => (
              <li>
                {ce.caseEventCategoryName} on {getEventDate(ce.createdDate)}
              </li>
            ))}
          </ol>
        );
      },
    },
    {
      Header: "Response",
      id: "statementOfIntentionToProceedCol",
      accessor: (row) => row.hasIntentionToProceed,
      style: { flexBasis: 1 },
      width: 90,
      Cell: (cell) => {
        if (!cell || !cell.original) return null;

        const row: LackOfProsecutionCaseSearchResult = cell.original;
        if (row.hasIntentionToProceed) {
          return <OkSign />;
        } else if (row.isCaseClosed) {
          return <CancelSign />;
        } else {
          return <QuestionSign />;
        }
      },
    },
    {
      Header: "Action",
      id: "actionButtonCol",
      width: 110,
      style: { flexBasis: 1 },
      Cell: (cell) => {
        return (
          <div className="centered">
            <Button onClick={() => showLetterModal(cell.original)}>
              Letters
            </Button>
          </div>
        );
      },
    },
  ];
  if (props.showLetterSelect) {
    columns.push({
      Header: "Print",
      id: "letterSelectCol",
      width: 60,
      style: { flexBasis: 1 },
      Cell: (cell) => {
        if (!cell || !cell.original) return null;
        let row: LackOfProsecutionCaseSearchResult = cell.original;
        return (
          <div className="centered">
            <Checkbox
              name="letterSelectChecbox"
              checked={props.caseIds.includes(row.caseID)}
              onChange={(e) => {
                if (e.currentTarget.checked) {
                  props.addLetterID(row.caseID);
                } else {
                  props.removeLetterID(row.caseID);
                }
              }}
            />
          </div>
        );
      },
    });
  }
  return (
    <div id="generate-letters-step">
      <LackOfProsecutionLettersModal
        show={showModal}
        letterCategories={props.letterCategories}
        caseResult={chosenCase}
        generateLetters={props.generateLetters}
        combineLetters={props.combineLetters}
        undoLetters={props.undoLetters}
        onHide={() => hideLetterModal()}
        signatures={props.signatures}
        defaultSignature={props.defaultSignatureId}
        hideDeadlineDate={props.hideDeadlineDate}
        lettersIds={props.caseIds}
        showPretrialDatePicker={props.showPretrialDatePicker}
      />

      <Panel
        title="Generate Letters"
        buttons={
          <>
            <HelpIconButton mode={HelpIconMode.Modal}>
              <p>
                Click the <strong>Letters</strong> button to create a letter
                &amp; docket entry for the contacts (attorneys, plaintiffs, pro
                se) on just a single case.
              </p>
              <p>
                Click the <strong>Generate All Letters</strong> button if you
                want to create a letter &amp; docket entry for every case in the
                list. Note: This will only generate if the case wasn't excluded
                on the previous step, and it doesn't already have a letter /
                docket of that type.
              </p>
              <p>
                To use the Combine All Letters feature, you must have previously
                generated letters and docket entries of that corresponding type.
              </p>
              <p>
                <strong>Please note:</strong> If any of the addresses on a case
                are confidential, the generated document that is attached to the
                docket entry will be marked as private / non-public.
              </p>
            </HelpIconButton>
            <Tooltip
              message="Please select case for a letter to print."
              requiresMessage={
                props.showLetterSelect && props.caseIds.length <= 0
              }
              disabledSubject
            >
              <Button
                name="lettersButton"
                onClick={() => showLetterModal()}
                disabled={
                  (props.showLetterSelect && props.caseIds.length <= 0) || false
                }
              >
                Generate All Letters
              </Button>
            </Tooltip>
          </>
        }
      >
        <Col xs={12}>
          <Checkbox
            checked={showOnlyEventsToggle}
            onChange={(e: any) => setShowOnlyEventsToggle(e.target.checked)}
          >
            Show Only Docketed Cases
          </Checkbox>
          <DataTable
            columns={columns}
            data={casesInTable}
            filterOn={["caseNumber", "caseCaption", "caseCategoryName"]}
            onFetchData={onFetchData}
            fetchDelay={1000}
            pages={props.totalPages || 0}
            totalRows={props.totalRows || 0}
            loading={props.loading}
          />
        </Col>
      </Panel>
    </div>
  );
};

(LackOfProsecutionGenerateLettersPanel as any).displayName =
  "LackOfProsecutionGenerateLettersPanel";
